import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, Moon, Plus, Sun } from 'lucide-react';

import PrivateLayout from '../../layouts/PrivateLayout';
import { cn } from '../../../lib/utils';
import { SOURCES } from '../../../lib/messageCore';
import { useMessages } from '../../../services/messages';
import { THEME_KEY } from '../clientOnboarding/clientOnboardingUtils';
import MessageEditor, { SAVE_BAR_HEIGHT } from './MessageEditor';
import { CARD, CHANNEL, OWN_KEY_RE, SOURCE_SHORT, checkMessage, usedLine, wordingLabel } from './messageUi';

// Messages (Nic 2026-10-22): every Sales and Onboarding message in one place,
// worded for where each person came from. Pick Sales or Onboarding, then
// Texts, Emails or Call scripts; each card says where the message is used and
// which wordings it has. Admins and the Onboarding Manager (messages:manage).

const readTheme = () => {
  try {
    return localStorage.getItem(THEME_KEY) || 'light';
  } catch {
    return 'light';
  }
};

const TEAMS = [
  { key: 'sales', label: 'Sales' },
  { key: 'onboarding', label: 'Onboarding' },
];

// Which wordings a message has, as chips (a red dot = needs a fix).
const WordingChips = ({ message, partners, limits }) => {
  const { byKey } = checkMessage(message, { limits, partners });
  if (message.mode === 'one') {
    return (
      <span className={cn('inline-flex items-center gap-[4px] rounded-full px-[8px] py-[2px] text-[11px] font-semibold', byKey.all ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-slate-900/[0.05] text-slate-600 dark:!bg-white/10 dark:text-slate-300')}>
        Same for everyone
      </span>
    );
  }
  // One chip when every source is fine (Nic 2026-10-22: five chips wrapped
  // the card); a source is named only when it needs a fix. A partner's or
  // event's own wording always shows.
  const badSources = SOURCES.filter((k) => byKey[k]);
  const own = Object.keys(message.wordings || {}).filter((k) => OWN_KEY_RE.test(k));
  return (
    <span className="flex flex-wrap gap-[4px]">
      {badSources.length ? null : (
        <span
          title={SOURCES.map((k) => SOURCE_SHORT[k]).join(', ')}
          className="inline-flex items-center gap-[3px] rounded-full bg-emerald-500/10 px-[7px] py-[1px] text-[11px] font-semibold text-emerald-700 dark:text-emerald-300"
        >
          <Check size={10} />
          All 5 sources
        </span>
      )}
      {[...badSources, ...own].map((k) => {
        const bad = !!byKey[k];
        const isOwn = OWN_KEY_RE.test(k);
        return (
          <span
            key={k}
            className={cn(
              'inline-flex items-center gap-[3px] rounded-full px-[7px] py-[1px] text-[11px] font-semibold',
              bad
                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
                : isOwn
                  ? 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                  : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
            )}
          >
            {bad ? <span className="h-[5px] w-[5px] rounded-full bg-rose-500" /> : <Check size={10} />}
            {isOwn ? wordingLabel(k, partners) : SOURCE_SHORT[k]}
          </span>
        );
      })}
    </span>
  );
};

// The list fills the window down to its bottom edge and scrolls inside
// itself (Nic 2026-10-22: the page shouldn't need its own scroll to show
// the end of the list). Measured, because the header above it wraps
// differently at every width. Below the lg breakpoint (stacked) it stays a
// short list above the editor.
const LIST_GAP = 16;
const useListHeight = (ref, deps) => {
  const [height, setHeight] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el || window.innerWidth < 1024) {
        setHeight(null);
        return;
      }
      // From the list's top on the page, not on screen, so scrolling the
      // page never changes it.
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Above the Save bar along the bottom of the window.
      setHeight(Math.max(240, window.innerHeight - top - SAVE_BAR_HEIGHT - LIST_GAP));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return height;
};

const MessagesPage = () => {
  const q = useMessages();
  const listRef = useRef(null);
  const data = q.data;
  const [searchParams, setSearchParams] = useSearchParams();
  const team = searchParams.get('team') === 'onboarding' ? 'onboarding' : 'sales';
  const channel = CHANNEL[searchParams.get('ch')] ? searchParams.get('ch') : 'text';
  const openId = searchParams.get('m');
  const [creating, setCreating] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    return () => root.classList.remove('dark');
  }, [theme]);
  const flipTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* private mode: this session only */
    }
  };

  // Leaving with unsaved words asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const okToLeave = () =>
    // eslint-disable-next-line no-alert
    !dirty || window.confirm("Your changes to this message aren't saved. Leave them?");

  const go = useCallback(
    (params) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(params).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const leaveTo = (params) => {
    if (!okToLeave()) return;
    setDirty(false);
    setCreating(false);
    go(params);
  };

  const messages = useMemo(() => data?.messages || [], [data]);
  // ?key=auto_nurture (Nic 2026-10-23: a Settings menu's Edit words opened
  // the page, not the message): open that message, on its team and channel.
  const wantKey = searchParams.get('key');
  useEffect(() => {
    if (!wantKey || !messages.length) return;
    const m = messages.find((x) => x.messageKey === wantKey);
    go({ key: null, ...(m ? { team: m.team === 'sales' ? null : m.team, ch: m.channel === 'text' ? null : m.channel, m: m.messageId } : {}) });
  }, [wantKey, messages, go]);
  const inTeam = messages.filter((m) => m.team === team);
  const shown = inTeam.filter((m) => m.channel === channel);
  const active = creating ? null : shown.find((m) => m.messageId === openId) || shown[0] || null;
  const listHeight = useListHeight(listRef, [q.isLoading, team, channel]);
  // The open message is always in view in the list (Nic 2026-10-23: Edit
  // words opened a message further down, out of sight). Scrolls the list
  // only, never the page, and only when it's out of view.
  const activeId = active?.messageId || null;
  useEffect(() => {
    const list = listRef.current;
    if (!list || !activeId) return;
    const el = list.querySelector(`[data-message-id="${activeId}"]`);
    if (!el) return;
    // Measured on screen: the list is sticky from lg, so offsetTop's parent
    // changes with the width.
    const top = el.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    const bottom = top + el.offsetHeight;
    if (top >= list.scrollTop && bottom <= list.scrollTop + list.clientHeight) return;
    list.scrollTo?.({ top: Math.max(0, top - 8), behavior: 'smooth' });
  }, [activeId, listHeight, shown.length]);
  const blank = { messageId: null, name: '', channel, team: 'sales', kind: 'cadence', mode: 'one', wordings: { all: { body: '' } }, fillIns: ['first_name', 'my_name', 'bio', 'owner_phone'], required: [], uses: [], switchOn: null };

  return (
    <PrivateLayout background={theme === 'dark' ? '#0E121B' : '#F5F7FA'} hideFooter fluid>
      {/* Room below for the Save bar fixed along the bottom. */}
      <div className="mx-auto w-full max-w-screen-2xl" style={{ paddingBottom: SAVE_BAR_HEIGHT + LIST_GAP }}>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="mb-0 text-xl font-bold text-slate-800 dark:text-slate-100">Messages</h2>
            <div className="mt-1.5 text-[12px] text-slate-500 dark:text-slate-400">
              Every message, worded for where each person came from: a partner, an event, our website, a referral, or a call.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full bg-white p-[3px] shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 dark:!bg-white/5 dark:ring-white/10">
              {TEAMS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => t.key !== team && leaveTo({ team: t.key === 'sales' ? null : t.key, ch: null, m: null })}
                  className={cn(
                    'rounded-full px-[14px] py-[5px] text-[12px] font-semibold outline-none focus:outline-none',
                    team === t.key ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={flipTheme}
              aria-label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 outline-none shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/80 hover:bg-slate-50 focus:outline-none dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        {q.isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : q.isError || !data ? (
          <div className={cn(CARD, 'p-[20px] text-sm text-rose-600')}>Messages couldn't be loaded. Refresh to try again.</div>
        ) : (
          <>
            {/* Texts | Emails | Call scripts, with how many of each */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {Object.entries(CHANNEL).map(([k, c]) => {
                const n = inTeam.filter((m) => m.channel === k).length;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => k !== channel && leaveTo({ ch: k === 'text' ? null : k, m: null })}
                    className={cn(
                      'inline-flex items-center gap-[6px] rounded-xl px-[12px] py-[6px] text-[13px] font-semibold outline-none ring-1 focus:outline-none',
                      channel === k
                        ? 'bg-white text-slate-900 shadow-sm ring-slate-300 dark:!bg-white/10 dark:text-white dark:ring-white/20'
                        : 'text-slate-500 ring-transparent hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100',
                    )}
                  >
                    <c.Icon size={14} />
                    {c.plural}
                    <span className="rounded-full bg-slate-900/[0.06] px-[6px] text-[11px] tabular-nums dark:!bg-white/10">{n}</span>
                  </button>
                );
              })}
              {team === 'sales' ? (
                <button
                  type="button"
                  onClick={() => {
                    if (!okToLeave()) return;
                    setDirty(false);
                    setCreating(true);
                  }}
                  className="ml-auto inline-flex h-8 items-center gap-[6px] rounded-xl bg-slate-900 px-[12px] text-[12px] font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none dark:!bg-slate-100 dark:!text-slate-900"
                >
                  <Plus size={14} /> New message
                </button>
              ) : null}
            </div>

            <div className="grid grid-cols-1 items-start gap-[12px] lg:grid-cols-[20rem_minmax(0,1fr)]">
              {/* The list: what each message is, where it's used, which words exist */}
              <div
                ref={listRef}
                style={listHeight ? { maxHeight: listHeight } : undefined}
                className="-m-[4px] flex max-h-[368px] flex-col gap-[8px] overflow-y-auto p-[4px] lg:sticky lg:top-[12px]"
              >
                {shown.length ? (
                  shown.map((m) => {
                    const on = !creating && active?.messageId === m.messageId;
                    return (
                      <button
                        key={m.messageId}
                        type="button"
                        data-message-id={m.messageId}
                        aria-current={on ? 'true' : undefined}
                        onClick={() => !on && leaveTo({ m: m.messageId })}
                        className={cn(
                          CARD,
                          'w-full px-[14px] py-[10px] text-left outline-none ring-2 focus:outline-none',
                          on ? 'ring-sky-400' : 'ring-transparent hover:ring-slate-200 dark:hover:ring-white/20',
                        )}
                      >
                        <div className="flex items-center gap-[6px]">
                          <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-slate-800 dark:text-slate-100">{m.name}</span>
                          {m.problems?.length ? <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-rose-500" title="Needs a fix" /> : null}
                        </div>
                        <div className="mb-[6px] truncate text-[11px] text-slate-500 dark:text-slate-400">{usedLine(m)}</div>
                        <WordingChips message={m} partners={data.partners} limits={data.limits} />
                      </button>
                    );
                  })
                ) : (
                  <div className={cn(CARD, 'p-[16px] text-[13px] text-slate-400')}>
                    No {CHANNEL[channel].plural.toLowerCase()} here yet.
                  </div>
                )}
              </div>

              {creating || active ? (
                <MessageEditor
                  key={creating ? `new-${channel}` : active.messageId}
                  message={creating ? blank : active}
                  data={data}
                  onDirtyChange={setDirty}
                  onSaved={(m) => {
                    setDirty(false);
                    if (creating && m?.messageId) {
                      setCreating(false);
                      go({ m: m.messageId, ch: m.channel === 'text' ? null : m.channel });
                    }
                  }}
                  onDeleted={() => {
                    setDirty(false);
                    go({ m: null });
                  }}
                />
              ) : (
                <div className={cn(CARD, 'p-[20px] text-sm text-slate-500')}>Pick a message.</div>
              )}
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
};

export default MessagesPage;
